// src/pages/SignupPage.jsx
import React, { useState, useEffect } from "react";
import { Box, TextField, Button, Typography, Container, Paper, Divider, Alert, IconButton, InputAdornment } from "@mui/material";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import GoogleIcon from "@mui/icons-material/Google";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

export default function SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signup, loginWithGoogle, isAuthenticated } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Pre-fill email if coming from home page
  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    const result = await signup(name, email, password);
    if (result.success) {
      navigate("/login");
    } else {
      setError(result.error || "Signup failed");
    }
    setLoading(false);
  };

  const handleGoogleSignup = () => {
    loginWithGoogle();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        py: 4,
      }}
    >
      {/* Background Effects */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(#1e3a8a 0.5px, transparent 0.5px),
            radial-gradient(#1e3a8a 0.5px, #ffffff 0.5px)
          `,
          backgroundSize: "40px 40px",
          backgroundPosition: "0 0, 20px 20px",
          opacity: 0.03,
        }}
      />

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        {/* Back Button */}
        <Box sx={{ mb: 2 }}>
          <IconButton
            onClick={() => navigate("/")}
            sx={{
              color: "#1e3a8a",
              bgcolor: "rgba(30, 58, 138, 0.05)",
              "&:hover": {
                bgcolor: "rgba(30, 58, 138, 0.1)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              bgcolor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 4,
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
          >
            {/* Logo */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: "rgba(30, 58, 138, 0.05)",
                  borderRadius: 999,
                  px: 2,
                  py: 1,
                  mb: 2,
                  border: "1px solid rgba(30, 58, 138, 0.1)",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    bgcolor: "#1e3a8a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ color: "white", fontWeight: "bold", fontSize: "20px" }}>
                    EXI
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ color: "#1e3a8a", fontWeight: "800" }}>
                  exivis
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: "#0f172a", fontWeight: 800, mb: 1 }}>
                Create Account
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                Join exivis and transform your engineering workflow
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, bgcolor: "#fef2f2", color: "#991b1b", border: "1px solid #fee2e2" }}>
                {(() => {
                  if (typeof error === "string") return error;
                  if (error?.message && typeof error.message === "string") return error.message;
                  if (error?.error && typeof error.error === "string") return error.error;
                  try {
                    return JSON.stringify(error);
                  } catch {
                    return "An error occurred";
                  }
                })()}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#cbd5e1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1e3a8a",
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#cbd5e1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1e3a8a",
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                helperText="Must be at least 8 characters"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: "#64748b" }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#cbd5e1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1e3a8a",
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        sx={{ color: "#64748b" }}
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#cbd5e1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1e3a8a",
                    },
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: "#1e3a8a",
                  color: "white",
                  py: 1.5,
                  mb: 2,
                  fontSize: "16px",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "12px",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#1e40af",
                    boxShadow: "none",
                  },
                }}
              >
                {loading ? "Creating Account..." : "Sign Up"}
              </Button>
            </form>

            <Divider sx={{ my: 3, borderColor: "#f1f5f9" }}>
              <Typography variant="body2" sx={{ color: "#94a3b8", px: 2, fontWeight: 600 }}>
                OR
              </Typography>
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              onClick={handleGoogleSignup}
              startIcon={<GoogleIcon />}
              sx={{
                borderColor: "#e2e8f0",
                color: "#475569",
                py: 1.5,
                mb: 3,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                "&:hover": {
                  borderColor: "#cbd5e1",
                  bgcolor: "#f8fafc",
                },
              }}
            >
              Continue with Google
            </Button>

            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                Already have an account?{" "}
                <Link
                  to="/login"
                  style={{
                    color: "#1e3a8a",
                    textDecoration: "none",
                    fontWeight: 700,
                  }}
                >
                  Sign In
                </Link>
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
}

